'use client'

import { Alert, Authenticator, useAuthenticator } from '@aws-amplify/ui-react'
import { redirect } from 'next/navigation'
import { useEffect, useState } from 'react'
import { updateIntercom } from '../../lib/intercom'
import {
  getClientByTenant,
  getLoggedInUserAttributes,
  isUserAuthorizedForClient,
  startSessionIfAuthorized,
} from '@/src/api'
import { Client } from '@/src/interfaces'
import { signOut } from 'aws-amplify/auth'
import { LoadingLogo } from '../layout/loading/LoadingLogo'

/**
 * Default components for the login page.
 * @see https://ui.docs.amplify.aws/react/connected-components/authenticator/customization
 */
const loginPageComponents = {
  Header: () => <>{/* Logo, etc */}</>,
  Footer: () => <> {/* Legal footer and policy, etc */}</>,
}

/**
 * Form fields with memberId for signup method 2
 */
const memberIdForm = {
  signUp: {
    'custom:memberId': {
      label: 'Member ID',
      placeholder: 'Please enter your Member ID',
      type: 'text',
      required: true,
      order: 1,
    },
  },
}

/**
 * A wrapper around the Authenticator component that authorizes the user
 * based on their login ID after login.
 *
 * @param {Object} props The component props.
 * @param {(user: any) => void} [props.onSuccess] The function to call after
 * the user is authorized.
 */
function UserAuthenticator({
  onSuccess,
  onError,
}: {
  onSuccess?: (user: any) => void
  onError?: (error: any) => void
}) {
  const { user } = useAuthenticator((context) => [context.user])
  const [isRedirecting, setIsRedirecting] = useState(false)
  // const [errorMessage, setErrorMessage] = useState<string | null>(null)

  useEffect(() => {
    /**
     * Authorize the user based on their login ID.
     */
    const authorizeUser = async () => {
      if (user?.signInDetails?.loginId) {
        try {
          const userAttributes = await getLoggedInUserAttributes(
            user.signInDetails.loginId
          )
          const { authorized, client, message } =
            await isUserAuthorizedForClient(userAttributes)
          if (authorized) {
            startSessionIfAuthorized(client)
            setIsRedirecting(true)
          } else {
            onError?.(message || 'You are not authorized to login.')
            await signOut()
          }
        } catch (error) {
          console.error('Authorization error:', error)
          await signOut()
        }
      }
    }
    authorizeUser()
  }, [user])

  useEffect(() => {
    if (isRedirecting) {
      onSuccess?.(user)
      redirect('/')
    }
  }, [isRedirecting, onSuccess])

  return <Authenticator />
}

/**
 * The Login component.
 *
 * This component renders the login page with a custom Authenticator component
 * that handles the login flow.
 */
export default function Login() {
  const [clientDetails, setClientDetails] = useState<Client>({
    id: '',
    signup_method: '1',
    sub_domain: '',
  })
  const [loading, setLoading] = useState(false)
  const [authError, setAuthError] = useState<string | null>(null)

  useEffect(() => {
    // Fetch tenant-specific data based on the subdomain
    async function fetchTenant() {
      setLoading(true)
      try {
        const data = await getClientByTenant()
        setClientDetails(data)
      } catch (error) {
        console.error('Error during fetch: ', error)
      } finally {
        setLoading(false)
      }
    }
    fetchTenant()
  }, [])

  // Handle successful login
  const handleSuccessfulLogin = (user: any) => {
    // Update Intercom with user data after successful login
    updateIntercom({
      userId: user.userId,
      name: user.username,
      email: user.signInDetails.loginId,
      createdAt: Math.floor(Date.now() / 1000),
    })
  }

  if (loading || !clientDetails) {
    return <LoadingLogo />
  }

  return (
    <div>
      {authError && (
        <Alert
          variation='error'
          isDismissible={true}
          hasIcon={true}
          heading='Login Failed'
          onDismiss={() => setAuthError(null)}
          className='fixed top-0 left-0 w-full z-50'
        >
          {authError}
        </Alert>
      )}
      <Authenticator
        formFields={clientDetails.signup_method === '2' ? memberIdForm : {}}
        components={loginPageComponents}
        className='h-screen'
      >
        <UserAuthenticator
          onSuccess={handleSuccessfulLogin}
          onError={setAuthError}
        />
      </Authenticator>
    </div>
  )
}
